import { useEffect, useState, useSyncExternalStore } from 'react';
import { activity } from '../../services/activity';
import BrandProgress from './BrandProgress';

/** True once `on` has been true for `showAfter` ms; stays true for at least `minShow` ms so quick calls never flicker. */
const useDelayed = (on, showAfter, minShow) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (on && !visible) {
      const timer = setTimeout(() => setVisible(true), showAfter);
      return () => clearTimeout(timer);
    }
    if (!on && visible) {
      const timer = setTimeout(() => setVisible(false), minShow);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [on, visible, showAfter, minShow]);
  return visible;
};

/**
 * App-wide brand progress for data work: a frosted-glass veil over the page with the round brand
 * loader centred on it. Saving or updating (writes) waits 400 ms and also blocks the page behind
 * the veil, which prevents double submits; loading (reads) waits 700 ms and lets clicks through.
 * Both wait a moment before showing, so fast requests stay invisible. Mounted once at the app
 * root and announced politely to assistive technology.
 */
export default function GlobalActivity() {
  const { pending, writing, locals } = useSyncExternalStore(activity.subscribe, activity.getSnapshot, activity.getSnapshot);
  const showLoad = useDelayed(pending > 0, 700, 500);
  const showSave = useDelayed(writing > 0, 400, 600);
  // One loader at a time: a screen-level loader already on screen takes over from this one.
  if ((!showLoad && !showSave) || locals > 0) return null;
  return (
    <div className={`bprog-glass${showSave ? ' is-blocking' : ''}`}>
      <div className="bprog-glass-core">
        <BrandProgress global variant="bare" size={88} label={showSave ? 'Saving changes' : 'Loading'} />
      </div>
    </div>
  );
}
